import moment from 'moment'
import { isArray } from 'lodash'

type Input = {
  workflow_input: number
  id: number
  uuid: string
  title: string
  parameters: {
    conditional_logic: {
      uuid: string
      conditions: [
        {
          criteria: 'in' | 'notIn'
          input: number
          values: []
          input_uuid: string
        },
      ]
      operator: 'and' | 'or'
    }
  }
  availability:
    | {
        evt_tasks: number[]
      }
    | []
  options?: Record<string, string>
}

type InputItem = {
  workflow_input?: number
  workflow_input_group: number
  id: number
  uuid: string
  input_item_type: 'input' | 'input_group'
  title: string
  short_name: string
  instructions: string
  image: string
  video: string
  type: string
  position: number
  required: boolean
  preview: boolean
  parameters: {
    conditional_logic: {
      uuid: string
      conditions: [
        {
          criteria: 'in' | 'notIn'
          input: number
          values: []
          input_uuid: string
        },
      ]
      operator: 'and' | 'or'
    }
  }
  availability:
    | {
        evt_tasks: number[]
      }
    | []
  inputs?: Input[]
  options?: Record<string, string>
}

type EvtInput = {
  id: null
  uuid: string
  input: number
  result: null | string | boolean | number | string[]
}

type EvtTask = {
  id: number
  uuid: string
  evt_inputs: EvtInput[]
}

type Task = {
  id: number
  uuid: string
  title: string
  input_items: InputItem[]
  evt_tasks: EvtTask[]
}

type Job = {
  id: number
  uuid: string
  workflow: number
  title: string
  tasks: Task[]
}

type FileResult = {
  file?: {
    s3DownloadUri: string
  }
  name?: string
}

type ImageResult = {
  s3DownloadUri?: string
  originalName: string
}

type JobData = {
  inputWorkflow: number
  inputTitle: string
  inputGroupWorkflow: number | null
  inputGroupTitle: string | null
  evtTask: number
  result: null | string | boolean | number | string[]
  parameters: {
    conditional_logic: {
      uuid: string
      conditions: [
        {
          criteria: 'in' | 'notIn'
          input: number
          values: []
          input_uuid: string
        },
      ]
      operator: 'and' | 'or'
    }
  }
  options?: Record<string, string>
  index?: number
  taskId: number
}

// Destructure necessary job details
const dateOfShipmentWorkflow = 301514
const orderWorkflow = 301515
const consigneeNameWorkflow = 301516
const consigneeAddressWorkflow = 301517
const deliveryLocationWorkflow = 301518
const deliveryAddressWorkflow = 301519
const specialInstructionsWorkflow = 301520

// Input Group Add to Address Book
const addToAddressConsigneeNameWorkflow = 302048
const addToAddressConsigneeAddressWorkflow = 302049
const addToAddressDeliveryLocationWorkflow = 302050
const addToAddressDeliveryAddressWorkflow = 302051
// end

const firstPart = [
  dateOfShipmentWorkflow,
  orderWorkflow,
  consigneeNameWorkflow,
  consigneeAddressWorkflow,
  specialInstructionsWorkflow,
  deliveryLocationWorkflow,
  deliveryAddressWorkflow,
  addToAddressConsigneeNameWorkflow,
  addToAddressConsigneeAddressWorkflow,
  addToAddressDeliveryLocationWorkflow,
  addToAddressDeliveryAddressWorkflow,
]

// Input Group Boxed
const boxesGroupWorkflow = 50568
// end

// Input Group Samples
const samplesGroupWorkflow = 50569
// end

const totalWeightWorkflow = 301679
const totalPalletsWorkflow = 301527
const palletCodesWorkflow = 301890

const fourPart = [
  totalWeightWorkflow,
  totalPalletsWorkflow,
  palletCodesWorkflow,
]

// Input Group Transport
const transportGroupWorkflow = 50570
// end

const vehicleConditionsAndChecksWorkflow = 301663
const vehicleTempWorkflow = 301532
const sixthPart = [vehicleConditionsAndChecksWorkflow, vehicleTempWorkflow]

// Input Group Pallet Temp
const palletTempGroupWorkflow = 50571
// end

const authorisedByWorkflow = 301534
const dispatchedByWorkflow = 301535

const isShow = (item: InputItem | Input, evtTask: EvtTask): boolean => {
  const { availability, parameters } = item
  if (!parameters || !parameters?.conditional_logic?.conditions) {
    return true
  }

  return Boolean(
    !isArray(availability) &&
      availability?.evt_tasks?.length &&
      availability?.evt_tasks.includes(evtTask.id),
  )
}

const toDataURL = (url: string) =>
  fetch(url)
    .then(response => response.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }),
    )

const getJobData = (tasks: Task[]): JobData[] => {
  const jobData: JobData[] = []

  tasks.forEach(({ input_items, evt_tasks: evtTasks }) => {
    input_items?.forEach((inputItem, index) => {
      const {
        workflow_input,
        parameters,
        title,
        workflow_input_group,
        id,
        inputs,
        options,
      } = inputItem
      evtTasks.forEach(evtTask => {
        const { evt_inputs: evtInputs, id: evtTaskId } = evtTask
        if (isShow(inputItem, evtTask)) {
          if (workflow_input) {
            let result: null | string | boolean | number | string[] = '-'
            const evtInput = evtInputs.find(ei => ei.input === id)
            if (evtInput && evtInput.result) {
              result = evtInput.result
              if (options) {
                if (
                  typeof evtInput.result === 'string' &&
                  options[evtInput.result]
                ) {
                  result = options[evtInput.result]
                }

                if (isArray(evtInput.result)) {
                  result = evtInput.result.map(res => options[res]).join(', ')
                }
              }
            }
            jobData.push({
              inputWorkflow: workflow_input,
              inputTitle: title,
              inputGroupWorkflow: null,
              inputGroupTitle: null,
              evtTask: evtTaskId,
              taskId: id,
              result,
              parameters,
            })
          } else if (workflow_input_group && inputs?.length) {
            inputs.forEach(input => {
              if (isShow(input, evtTask)) {
                let result: null | string | boolean | number | string[] = '-'
                const evtInput = evtInputs.find(ei => ei.input === input.id)
                if (evtInput && evtInput.result) {
                  result = evtInput.result
                  if (
                    options &&
                    typeof evtInput.result === 'string' &&
                    options[evtInput.result]
                  ) {
                    result = options[evtInput.result]
                  }
                }
                jobData.push({
                  inputWorkflow: input.workflow_input,
                  inputTitle: input.title,
                  inputGroupWorkflow: workflow_input_group,
                  inputGroupTitle: title,
                  evtTask: evtTaskId,
                  result,
                  parameters: input.parameters,
                  taskId: id,
                  index,
                })
              }
            })
          }
        }
      })
    })
  })

  return jobData
}

const getJobInputData = (jobData: JobData[], inputId: number) =>
  jobData.find(({ inputWorkflow }) => inputWorkflow === inputId)

const getInputGroupData = (jobData: JobData[], inputGroup: number) => {
  const inputGroupsData = jobData.filter(
    ({ inputGroupWorkflow }) => inputGroup === inputGroupWorkflow,
  )

  if (inputGroupsData.length) {
    const data: {
      title: string | null
      inputTitles: string[]
      results: Array<
        Record<string, null | string | boolean | number | string[]>
      >
    } = {
      title: inputGroupsData[0].inputGroupTitle,
      inputTitles: [],
      results: [],
    }

    const inputTitles: string[] = []

    let minIndex = Infinity
    inputGroupsData.forEach(input => {
      if (input.index !== undefined) {
        minIndex = Math.min(minIndex, input.index)
      }
    })

    inputGroupsData.forEach(input => {
      if (!inputTitles.includes(input.inputTitle)) {
        inputTitles.push(input.inputTitle)
      }

      if (input.index !== undefined) {
        const adjustedIndex = input.index - minIndex
        const result: Record<
          string,
          null | string | boolean | number | string[]
        > = {}
        result[input.inputTitle] = input.result

        if (!data.results[adjustedIndex]) {
          data.results[adjustedIndex] = {}
        }

        Object.assign(data.results[adjustedIndex], result)
      }
    })

    data.inputTitles = inputTitles

    return data
  }

  return undefined
}

const generateHTMLTemplate = async (job: Job, userName: string) => {
  const { title, tasks } = job
  const dateInFormat = moment().format('YYYY-MM-DD')
  const jobData = getJobData(tasks)
  const firstPartData = firstPart.map(id => getJobInputData(jobData, id))
  const secondPartData = getInputGroupData(jobData, boxesGroupWorkflow)
  const thirdPartData = getInputGroupData(jobData, samplesGroupWorkflow)
  const fourPartData = fourPart.map(id => getJobInputData(jobData, id))
  const fifthPartData = getInputGroupData(jobData, transportGroupWorkflow)
  const sixthPartData = sixthPart.map(id => getJobInputData(jobData, id))
  const seventhPartData = getInputGroupData(jobData, palletTempGroupWorkflow)
  const authorisedByData = getJobInputData(jobData, authorisedByWorkflow)
  const dispatchedByData = getJobInputData(jobData, dispatchedByWorkflow)
  const authorisedByDataResult = (authorisedByData?.result as FileResult)?.file
    ?.s3DownloadUri
    ? await toDataURL(
        (authorisedByData?.result as FileResult)?.file?.s3DownloadUri as string,
      )
    : 'signature'
  const authorisedByDataName =
    (authorisedByData?.result as FileResult)?.name ?? userName
  const dispatchedByDataResult = (dispatchedByData?.result as FileResult)?.file
    ?.s3DownloadUri
    ? await toDataURL(
        (dispatchedByData?.result as FileResult)?.file?.s3DownloadUri as string,
      )
    : 'signature'
  const dispatchedByDataName =
    (dispatchedByData?.result as FileResult)?.name ?? userName

  return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Dispatch Note</title>
      </head>
      <body style="font-size: 19px; font-family: Helvetica Neue; font-weight: 400; padding: 40px;">
        <div style="display: flex; justify-content: space-between; align-items: center; height: 100%;">
            <div class="doc-meta">${dateInFormat}</div>
            <div>
                <svg width="91" height="22" viewBox="0 0 91 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M35.5714 21.5986C33.7147 21.5986 32.1488 21.2179 30.8738 20.4566C29.5987 19.6841 28.6312 18.6204 27.9713 17.2657C27.3226 15.911 26.9983 14.3547 26.9983 12.5969C26.9983 10.7832 27.3338 9.19331 28.0049 7.82738C28.676 6.45026 29.6379 5.38663 30.8905 4.63649C32.1544 3.87515 33.67 3.49449 35.4371 3.49449C38.0991 3.49449 40.1627 4.3286 41.6279 5.99682C43.0931 7.66504 43.8537 9.95465 43.9096 12.8656C43.9096 13.112 43.7921 13.2351 43.5573 13.2351H31.0751C31.1534 15.1608 31.5728 16.5827 32.3334 17.5008C33.0939 18.4189 34.19 18.878 35.6217 18.878C36.6842 18.878 37.5511 18.6036 38.2221 18.055C38.8932 17.5064 39.3518 16.6835 39.5979 15.5863C39.6538 15.3736 39.7824 15.2672 39.9837 15.2672H43.1546C43.423 15.2672 43.5405 15.3624 43.5069 15.5527C43.3503 16.6723 42.9365 17.6912 42.2654 18.6092C41.6055 19.5273 40.7107 20.2551 39.5811 20.7925C38.4514 21.3299 37.1149 21.5986 35.5714 21.5986ZM31.1925 10.6152H39.5643C39.5084 9.83149 39.3238 9.10374 39.0107 8.43197C38.7087 7.76021 38.2613 7.22279 37.6685 6.81973C37.0757 6.40548 36.3431 6.19835 35.4707 6.19835C34.2292 6.19835 33.2561 6.57902 32.5515 7.34035C31.8468 8.10169 31.3939 9.19331 31.1925 10.6152Z" fill="#0A0810"/>
                    <path d="M54.0332 21.5986C52.2437 21.5986 50.717 21.2291 49.4531 20.4902C48.2004 19.7512 47.2385 18.71 46.5674 17.3665C45.9075 16.0117 45.5776 14.4219 45.5776 12.5969C45.5776 10.772 45.9075 9.17652 46.5674 7.81059C47.2385 6.44466 48.2116 5.38663 49.4866 4.63649C50.7617 3.87515 52.3052 3.49449 54.1171 3.49449C56.2758 3.49449 58.0094 4.05429 59.318 5.1739C60.6378 6.28232 61.3984 7.75461 61.5997 9.59077C61.6445 9.83709 61.5326 9.96024 61.2642 9.96024H58.2443C58.0206 9.96024 57.892 9.84828 57.8584 9.62436C57.6459 8.49355 57.1985 7.64825 56.5163 7.08844C55.834 6.52863 55.0343 6.24873 54.1171 6.24873C52.6296 6.24873 51.5223 6.79174 50.7953 7.87777C50.0794 8.96379 49.7215 10.5312 49.7215 12.5801C49.7215 14.6738 50.085 16.2413 50.812 17.2825C51.539 18.3237 52.6351 18.8556 54.1003 18.878C55.1741 18.8891 56.0633 18.5589 56.7679 17.8871C57.4725 17.2153 57.892 16.2469 58.0262 14.9817C58.0597 14.7466 58.1828 14.629 58.3953 14.629H61.4823C61.7507 14.629 61.8681 14.741 61.8346 14.9649C61.7116 16.2525 61.3145 17.4001 60.6434 18.4077C59.9723 19.4042 59.0776 20.1879 57.9591 20.7589C56.8406 21.3187 55.532 21.5986 54.0332 21.5986Z" fill="#0A0810"/>
                    <path d="M82.5108 21.5986C80.6877 21.5986 79.1386 21.2291 77.8635 20.4902C76.5997 19.7512 75.6322 18.7044 74.9611 17.3497C74.29 15.9949 73.9545 14.4051 73.9545 12.5801C73.9545 10.7664 74.29 9.17652 74.9611 7.81059C75.6322 6.44466 76.5997 5.38663 77.8635 4.63649C79.1386 3.87515 80.6877 3.49449 82.5108 3.49449C84.3227 3.49449 85.8606 3.86396 87.1245 4.6029C88.3884 5.34185 89.3503 6.38868 90.0102 7.74341C90.6701 9.09814 91 10.688 91 12.513C91 14.3379 90.6701 15.9334 90.0102 17.2993C89.3503 18.6652 88.3884 19.7233 87.1245 20.4734C85.8606 21.2235 84.3227 21.5986 82.5108 21.5986ZM82.5108 18.878C83.976 18.878 85.0665 18.3517 85.7823 17.2993C86.4981 16.2469 86.8561 14.6738 86.8561 12.5801C86.8561 10.52 86.4926 8.94699 85.7655 7.86097C85.0385 6.76375 83.9424 6.21514 82.4772 6.21514C80.9897 6.22634 79.8824 6.78055 79.1554 7.87777C78.4395 8.96379 78.0816 10.5424 78.0816 12.6137C78.0816 14.685 78.4507 16.2469 79.1889 17.2993C79.9271 18.3517 81.0344 18.878 82.5108 18.878Z" fill="#0A0810"/>
                    <path d="M70.1067 21.4978C69.2008 21.4978 68.3563 21.3355 67.5734 21.0108C66.7905 20.6749 66.1529 20.1263 65.6608 19.365C65.1799 18.5925 64.9394 17.568 64.9394 16.2916L64.9562 6.80294H63.0771C62.8423 6.80294 62.7248 6.67978 62.7248 6.43347V5.57421V4.76353C62.7248 4.55123 62.8997 4.37516 63.1101 4.3486C64.506 4.17246 66.4798 3.33689 66.6537 0.391959C66.6665 0.176393 66.84 0 67.0557 0H68.5499C68.7656 0 68.9405 0.175056 68.9405 0.390999V3.89755H72.7911C73.0372 3.89755 73.1602 4.0151 73.1602 4.25022V6.43347C73.1602 6.67978 73.0372 6.80294 72.7911 6.80294H68.9324L68.9659 16.1909C68.9659 17.0978 69.1393 17.7248 69.486 18.0718C69.8327 18.4077 70.3249 18.5757 70.9624 18.5757C71.3091 18.5757 71.6279 18.5589 71.9187 18.5253C72.2207 18.4805 72.4891 18.4245 72.724 18.3573C72.9924 18.279 73.1266 18.3797 73.1266 18.6596V20.6245C73.1266 20.8373 73.0372 20.9716 72.8582 21.0276C72.4444 21.162 71.997 21.2739 71.516 21.3635C71.0463 21.4531 70.5765 21.4978 70.1067 21.4978Z" fill="#0A0810"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.9662 20.9049C18.1047 20.6331 18.1047 20.2773 18.1047 19.5658L18.1047 5.52671C18.1047 4.81516 18.1047 4.45938 17.9662 4.18761C17.8444 3.94855 17.6501 3.75418 17.411 3.63237C17.1392 3.4939 16.7835 3.4939 16.0719 3.4939L2.03281 3.4939C1.32126 3.4939 0.965486 3.4939 0.69371 3.63237C0.454649 3.75418 0.260286 3.94854 0.138478 4.1876C1.32073e-06 4.45938 1.28963e-06 4.81516 1.22742e-06 5.5267L0 19.5658C-6.21225e-08 20.2773 -9.32254e-08 20.6331 0.138477 20.9049C0.260284 21.144 0.454647 21.3383 0.693708 21.4601C0.965484 21.5986 1.32126 21.5986 2.03281 21.5986L16.0719 21.5986C16.7834 21.5986 17.1392 21.5986 17.411 21.4601C17.6501 21.3383 17.8444 21.144 17.9662 20.9049ZM2.45818 18.8709C2.3822 18.7298 2.3822 18.5532 2.3822 18.2L2.3822 6.89251C2.3822 6.53673 2.3822 6.35885 2.45144 6.22296C2.51235 6.10343 2.60953 6.00625 2.72906 5.94534C2.86495 5.8761 3.04283 5.8761 3.39861 5.8761L14.7061 5.8761C15.0593 5.8761 15.236 5.8761 15.377 5.95208C15.4951 6.0157 15.6019 6.13093 15.6563 6.25354C15.7213 6.39996 15.7088 6.56382 15.6838 6.89152V6.89154C15.5766 8.29569 15.2476 9.67623 14.707 10.9812C14.0366 12.5997 13.054 14.0704 11.8152 15.3091C10.5765 16.5479 9.10584 17.5305 7.48732 18.2009C6.18233 18.7415 4.8018 19.0705 3.39764 19.1777C3.06992 19.2027 2.90606 19.2152 2.75964 19.1502C2.63703 19.0958 2.5218 18.989 2.45818 18.8709Z" fill="#0A0810"/>
                </svg>
            </div>
        </div>
        <div style="font-size:1.5em;margin:24px 0;text-align:center;"><strong>${title}</strong></div>
        <div style="display:flex;flex-wrap:wrap;">
            ${firstPartData
              .filter(item => item)
              .map(
                item => `
                <div style="width:50%; display:flex; margin:4px 0;">
                   <div style="width:50%;"><strong>${item?.inputTitle}</strong></div>
                   <div style="width:50%;">${item?.result}</div>
                </div>
            `,
              )
              .join('')}
        </div>
        <p>&nbsp;</p>
        ${
          secondPartData &&
          `
        <div style="font-size:1.0em;margin:8px 0;"><strong>${
          secondPartData?.title
        }</strong></div>
        <figure class="table" style="width:100%;">
            <table style="border:2px solid black; border-collapse:collapse; width: 100%;">
                <thead>
                    <tr>
                    ${secondPartData?.inputTitles
                      .map(
                        title => `
                        <th style="border:2px solid black; padding:8px;text-align:left;">${title}</th>`,
                      )
                      .join('')}
                    </tr>
                </thead>
                <tbody>
                  ${(() => {
                    let content = ''
                    secondPartData?.results.forEach(result => {
                      content += '<tr>'
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      content += secondPartData?.inputTitles
                        .map(
                          title =>
                            `<td style="border:2px solid black; padding:8px;text-align:left;">${result[title]}</td>`,
                        )
                        .join('')
                    })
                    content += '</tr>'

                    return content
                  })()}
                </tbody>
            </table>
        </figure>
        `
        }
        ${
          thirdPartData &&
          `
        <div style="font-size:1.0em;margin:8px 0;"><strong>${
          thirdPartData?.title
        }</strong></div>
        <figure class="table" style="width:100%;">
            <table style="border:2px solid black; border-collapse:collapse; width: 100%;">
                <thead>
                    <tr>
                    ${thirdPartData?.inputTitles
                      .map(
                        title => `
                        <th style="border:2px solid black; padding:8px;text-align:left;">${title}</th>`,
                      )
                      .join('')}
                    </tr>
                </thead>
                <tbody>
                  ${(() => {
                    let content = ''
                    thirdPartData?.results.forEach(result => {
                      content += '<tr>'
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      content += thirdPartData?.inputTitles
                        .map(
                          title =>
                            `<td style="border:2px solid black; padding:8px;text-align:left;">${
                              result[title] ? result[title] : '-'
                            }</td>`,
                        )
                        .join('')
                    })
                    content += '</tr>'

                    return content
                  })()}
                </tbody>
            </table>
        </figure>
        `
        }
        <p>&nbsp;</p>
        ${fourPartData
          .map(
            item => `
                        <div style="font-size:1.0em;margin:8px 0;"><strong>${item?.inputTitle} </strong>${item?.result}</div>
                    `,
          )
          .join('')}
        <p>&nbsp;</p>
        ${
          fifthPartData &&
          `
        <div style="font-size:1.0em;margin:8px 0;"><strong>${
          fifthPartData?.title
        }</strong></div>
        <figure class="table" style="width:100%;">
            <table style="border:2px solid black; border-collapse:collapse; width: 100%;">
                <thead>
                    <tr>
                    ${fifthPartData?.inputTitles
                      .map(
                        title => `
                        <th style="border:2px solid black; padding:8px;text-align:left;">${title}</th>`,
                      )
                      .join('')}
                    </tr>
                </thead>
                <tbody>
                  ${(() => {
                    let content = ''
                    fifthPartData?.results.forEach(result => {
                      content += '<tr>'
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      content += fifthPartData?.inputTitles
                        .map(
                          title =>
                            `<td style="border:2px solid black; padding:8px;text-align:left;">${result[title]}</td>`,
                        )
                        .join('')
                    })
                    content += '</tr>'

                    return content
                  })()}
                </tbody>
            </table>
        </figure>
        `
        }
        <p>&nbsp;</p>
        ${sixthPartData
          .map(
            item => `
                        <div style="font-size:1.0em;margin:8px 0;"><strong>${item?.inputTitle} </strong>${item?.result}</div>
                    `,
          )
          .join('')}
        <p>&nbsp;</p>
        ${
          seventhPartData &&
          `
        <div style="font-size:1.0em;margin:8px 0;"><strong>${
          seventhPartData?.title
        }</strong></div>
        <figure class="table" style="width:100%;">
            <table style="border:2px solid black; border-collapse:collapse; width: 100%;">
                <thead>
                    <tr>
                    ${seventhPartData?.inputTitles
                      .map(
                        title => `
                        <th style="border:2px solid black; padding:8px;text-align:left;">${title}</th>`,
                      )
                      .join('')}
                    </tr>
                </thead>
                <tbody>
                  ${(() => {
                    let content = ''
                    seventhPartData?.results.forEach(result => {
                      content += '<tr>'
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      content += seventhPartData?.inputTitles
                        .map(
                          title =>
                            `<td style="border:2px solid black; padding:8px;text-align:left;">${result[title]}</td>`,
                        )
                        .join('')
                    })
                    content += '</tr>'

                    return content
                  })()}
                </tbody>
            </table>
        </figure>
        `
        }
        <p>&nbsp;</p>
        <div style="display:flex;flex-wrap:wrap;">
            <div style="width:50%; display:flex; margin:4px 0;">
                <div style="text-align:right;width:50%;"><strong>Authorised By:&#160</strong></div>
                <div style="width:50%;">${authorisedByDataName}</div>
            </div>
            <div style="width:50%; display:flex; margin:4px 0;">
                <div style="text-align:right;width:50%;"><strong>Dispatched By:&#160</strong></div>
                <div style="width:50%;">${dispatchedByDataName}</div>
            </div>
            <div style="width:50%; display:flex; margin:4px 0;">
                <div style="text-align:right;width:50%;"><img src="${authorisedByDataResult}" alt="authorised signature" width="91"></div>
            </div>
            <div style="width:50%; display:flex; margin:4px 0;">
                <div style="text-align:right;width:50%;"><img src="${dispatchedByDataResult}" alt="dispatched signature" width="91"></div>
            </div>
        </div>
      </body>
      </html>
    `
}

const FISH_GROUP_ID = 303581
const CAGE = 303582
const DATA_COLLECTION_GROUP = 50897
const NUMBER_OF_FISH = 303583
const PICTURES = 303584
const COMMENTS = 303585

const buildDataTableHTML = async (jobData: JobData[]): Promise<string> => {
  const groupsByEvtTask: Record<number, JobData[]> = {}

  jobData.forEach(item => {
    if (!groupsByEvtTask[item.evtTask]) groupsByEvtTask[item.evtTask] = []
    groupsByEvtTask[item.evtTask].push(item)
  })

  let tableHTML = `
  <div style="font-size:1.0em;margin:8px 0;"><strong>Section 2</strong></div>
  <figure class="table" style="width:100%;">
      <table style="border:1px solid black; border-collapse:collapse; width: 100%;">
          <thead>
              <tr>
                  <th style="border:1px solid black; padding:8px;text-align:left;width:20%">Fish Group ID</th>
                  <th style="border:1px solid black; padding:8px;text-align:left;width:10%">Cage</th>
                  <th style="border:1px solid black; padding:8px;text-align:left;width:20%">Pictures</th>
                  <th style="border:1px solid black; padding:8px;text-align:left;width:35%">Comments</th>
                  <th style="border:1px solid black; padding:8px;text-align:left;width:15%">Number of fish</th>
              </tr>
          </thead>
          <tbody>
  `

  let totalNumberOfFish = 0

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const evtTaskId in groupsByEvtTask) {
    const group = groupsByEvtTask[evtTaskId]

    const fishGroupIdItem = group.find(
      item => item.inputWorkflow === FISH_GROUP_ID,
    )

    // eslint-disable-next-line no-continue
    if (!fishGroupIdItem) continue

    const cageItem = group.find(item => item.inputWorkflow === CAGE)

    const fishGroupIdValue = fishGroupIdItem?.result || '-'
    const cageValue = cageItem?.result || '-'

    const dataCollectionItems = group.filter(
      item => item.inputGroupWorkflow === DATA_COLLECTION_GROUP,
    )

    const rowsByIndex: Record<number, JobData[]> = {}
    dataCollectionItems.forEach(item => {
      if (item.index === undefined) return
      if (!rowsByIndex[item.index]) rowsByIndex[item.index] = []
      rowsByIndex[item.index].push(item)
    })

    const sortedIndices = Object.keys(rowsByIndex)
      .map(k => parseInt(k, 10))
      .sort((a, b) => a - b)

    const rowCount = sortedIndices.length || 1
    let firstRow = true
    // eslint-disable-next-line no-restricted-syntax
    for (const idx of sortedIndices) {
      const rowItems = rowsByIndex[idx]
      const numberOfFishItem = rowItems.find(
        i => i.inputWorkflow === NUMBER_OF_FISH,
      )
      const picturesItem = rowItems.find(i => i.inputWorkflow === PICTURES)
      const commentsItem = rowItems.find(i => i.inputWorkflow === COMMENTS)

      const numberOfFishValue = numberOfFishItem?.result ?? '-'
      let picturesValue = picturesItem?.result ?? '-'

      if (
        typeof picturesValue === 'object' &&
        picturesValue !== null &&
        'originalName' in picturesValue
      ) {
        picturesValue = (picturesValue as ImageResult)?.s3DownloadUri
          ? // eslint-disable-next-line no-await-in-loop
            `<img src="${await toDataURL(
              (picturesValue as ImageResult)?.s3DownloadUri as string,
            )}" alt="authorised signature" width="91">`
          : (picturesValue as ImageResult).originalName
      }

      const commentsValue = commentsItem?.result ?? '-'

      // Add to total number of fish
      if (typeof numberOfFishValue === 'number') {
        totalNumberOfFish += numberOfFishValue
      }

      tableHTML += '<tr>'

      if (firstRow) {
        tableHTML += `
          <td style="border:1px solid black; padding:8px;text-align:left;" rowspan="${rowCount}">${fishGroupIdValue}</td>
          <td style="border:1px solid black; padding:8px;text-align:left;" rowspan="${rowCount}">${cageValue}</td>
        `
        firstRow = false
      }

      tableHTML += `
          <td style="border:1px solid black; padding:8px;text-align:left;">${picturesValue}</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">${commentsValue}</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">${numberOfFishValue}</td>
      `

      tableHTML += '</tr>'
    }

    if (sortedIndices.length === 0) {
      tableHTML += `
        <tr>
          <td style="border:1px solid black; padding:8px;text-align:left;">${fishGroupIdValue}</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">${cageValue}</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">-</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">-</td>
          <td style="border:1px solid black; padding:8px;text-align:left;">-</td>
        </tr>`
    }
  }

  tableHTML += `
          <tr>
              <td colspan="4" style="border:1px solid black; padding:8px;text-align:right;"><strong>Total Number of Fish</strong></td>
              <td style="border:1px solid black; padding:8px;text-align:left;"><strong>${totalNumberOfFish}</strong></td>
          </tr>
  `

  tableHTML += `
          </tbody>
      </table>
  </figure>
  `

  return tableHTML
}

export const generateHTMLTemplateForWorkflow9733 = async (job: Job) => {
  const { title, tasks } = job
  const dateInFormat = moment().format('YYYY-MM-DD')
  const jobData = getJobData(tasks)
  const firstPartIds = [303579, 303580]
  const firstPartData = firstPartIds.map(id => getJobInputData(jobData, id))
  const tableHTML = await buildDataTableHTML(jobData)

  return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Onsite Field Gathering Checklist for Autopsies</title>
      </head>
      <body style="font-size: 19px; font-family: Helvetica Neue; font-weight: 400; padding: 40px;">
        <div style="display: flex; justify-content: space-between; align-items: center; height: 100%;">
            <div class="doc-meta">${dateInFormat}</div>
            <div>
                <svg width="91" height="22" viewBox="0 0 91 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M35.5714 21.5986C33.7147 21.5986 32.1488 21.2179 30.8738 20.4566C29.5987 19.6841 28.6312 18.6204 27.9713 17.2657C27.3226 15.911 26.9983 14.3547 26.9983 12.5969C26.9983 10.7832 27.3338 9.19331 28.0049 7.82738C28.676 6.45026 29.6379 5.38663 30.8905 4.63649C32.1544 3.87515 33.67 3.49449 35.4371 3.49449C38.0991 3.49449 40.1627 4.3286 41.6279 5.99682C43.0931 7.66504 43.8537 9.95465 43.9096 12.8656C43.9096 13.112 43.7921 13.2351 43.5573 13.2351H31.0751C31.1534 15.1608 31.5728 16.5827 32.3334 17.5008C33.0939 18.4189 34.19 18.878 35.6217 18.878C36.6842 18.878 37.5511 18.6036 38.2221 18.055C38.8932 17.5064 39.3518 16.6835 39.5979 15.5863C39.6538 15.3736 39.7824 15.2672 39.9837 15.2672H43.1546C43.423 15.2672 43.5405 15.3624 43.5069 15.5527C43.3503 16.6723 42.9365 17.6912 42.2654 18.6092C41.6055 19.5273 40.7107 20.2551 39.5811 20.7925C38.4514 21.3299 37.1149 21.5986 35.5714 21.5986ZM31.1925 10.6152H39.5643C39.5084 9.83149 39.3238 9.10374 39.0107 8.43197C38.7087 7.76021 38.2613 7.22279 37.6685 6.81973C37.0757 6.40548 36.3431 6.19835 35.4707 6.19835C34.2292 6.19835 33.2561 6.57902 32.5515 7.34035C31.8468 8.10169 31.3939 9.19331 31.1925 10.6152Z" fill="#0A0810"/>
                    <path d="M54.0332 21.5986C52.2437 21.5986 50.717 21.2291 49.4531 20.4902C48.2004 19.7512 47.2385 18.71 46.5674 17.3665C45.9075 16.0117 45.5776 14.4219 45.5776 12.5969C45.5776 10.772 45.9075 9.17652 46.5674 7.81059C47.2385 6.44466 48.2116 5.38663 49.4866 4.63649C50.7617 3.87515 52.3052 3.49449 54.1171 3.49449C56.2758 3.49449 58.0094 4.05429 59.318 5.1739C60.6378 6.28232 61.3984 7.75461 61.5997 9.59077C61.6445 9.83709 61.5326 9.96024 61.2642 9.96024H58.2443C58.0206 9.96024 57.892 9.84828 57.8584 9.62436C57.6459 8.49355 57.1985 7.64825 56.5163 7.08844C55.834 6.52863 55.0343 6.24873 54.1171 6.24873C52.6296 6.24873 51.5223 6.79174 50.7953 7.87777C50.0794 8.96379 49.7215 10.5312 49.7215 12.5801C49.7215 14.6738 50.085 16.2413 50.812 17.2825C51.539 18.3237 52.6351 18.8556 54.1003 18.878C55.1741 18.8891 56.0633 18.5589 56.7679 17.8871C57.4725 17.2153 57.892 16.2469 58.0262 14.9817C58.0597 14.7466 58.1828 14.629 58.3953 14.629H61.4823C61.7507 14.629 61.8681 14.741 61.8346 14.9649C61.7116 16.2525 61.3145 17.4001 60.6434 18.4077C59.9723 19.4042 59.0776 20.1879 57.9591 20.7589C56.8406 21.3187 55.532 21.5986 54.0332 21.5986Z" fill="#0A0810"/>
                    <path d="M82.5108 21.5986C80.6877 21.5986 79.1386 21.2291 77.8635 20.4902C76.5997 19.7512 75.6322 18.7044 74.9611 17.3497C74.29 15.9949 73.9545 14.4051 73.9545 12.5801C73.9545 10.7664 74.29 9.17652 74.9611 7.81059C75.6322 6.44466 76.5997 5.38663 77.8635 4.63649C79.1386 3.87515 80.6877 3.49449 82.5108 3.49449C84.3227 3.49449 85.8606 3.86396 87.1245 4.6029C88.3884 5.34185 89.3503 6.38868 90.0102 7.74341C90.6701 9.09814 91 10.688 91 12.513C91 14.3379 90.6701 15.9334 90.0102 17.2993C89.3503 18.6652 88.3884 19.7233 87.1245 20.4734C85.8606 21.2235 84.3227 21.5986 82.5108 21.5986ZM82.5108 18.878C83.976 18.878 85.0665 18.3517 85.7823 17.2993C86.4981 16.2469 86.8561 14.6738 86.8561 12.5801C86.8561 10.52 86.4926 8.94699 85.7655 7.86097C85.0385 6.76375 83.9424 6.21514 82.4772 6.21514C80.9897 6.22634 79.8824 6.78055 79.1554 7.87777C78.4395 8.96379 78.0816 10.5424 78.0816 12.6137C78.0816 14.685 78.4507 16.2469 79.1889 17.2993C79.9271 18.3517 81.0344 18.878 82.5108 18.878Z" fill="#0A0810"/>
                    <path d="M70.1067 21.4978C69.2008 21.4978 68.3563 21.3355 67.5734 21.0108C66.7905 20.6749 66.1529 20.1263 65.6608 19.365C65.1799 18.5925 64.9394 17.568 64.9394 16.2916L64.9562 6.80294H63.0771C62.8423 6.80294 62.7248 6.67978 62.7248 6.43347V5.57421V4.76353C62.7248 4.55123 62.8997 4.37516 63.1101 4.3486C64.506 4.17246 66.4798 3.33689 66.6537 0.391959C66.6665 0.176393 66.84 0 67.0557 0H68.5499C68.7656 0 68.9405 0.175056 68.9405 0.390999V3.89755H72.7911C73.0372 3.89755 73.1602 4.0151 73.1602 4.25022V6.43347C73.1602 6.67978 73.0372 6.80294 72.7911 6.80294H68.9324L68.9659 16.1909C68.9659 17.0978 69.1393 17.7248 69.486 18.0718C69.8327 18.4077 70.3249 18.5757 70.9624 18.5757C71.3091 18.5757 71.6279 18.5589 71.9187 18.5253C72.2207 18.4805 72.4891 18.4245 72.724 18.3573C72.9924 18.279 73.1266 18.3797 73.1266 18.6596V20.6245C73.1266 20.8373 73.0372 20.9716 72.8582 21.0276C72.4444 21.162 71.997 21.2739 71.516 21.3635C71.0463 21.4531 70.5765 21.4978 70.1067 21.4978Z" fill="#0A0810"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.9662 20.9049C18.1047 20.6331 18.1047 20.2773 18.1047 19.5658L18.1047 5.52671C18.1047 4.81516 18.1047 4.45938 17.9662 4.18761C17.8444 3.94855 17.6501 3.75418 17.411 3.63237C17.1392 3.4939 16.7835 3.4939 16.0719 3.4939L2.03281 3.4939C1.32126 3.4939 0.965486 3.4939 0.69371 3.63237C0.454649 3.75418 0.260286 3.94854 0.138478 4.1876C1.32073e-06 4.45938 1.28963e-06 4.81516 1.22742e-06 5.5267L0 19.5658C-6.21225e-08 20.2773 -9.32254e-08 20.6331 0.138477 20.9049C0.260284 21.144 0.454647 21.3383 0.693708 21.4601C0.965484 21.5986 1.32126 21.5986 2.03281 21.5986L16.0719 21.5986C16.7834 21.5986 17.1392 21.5986 17.411 21.4601C17.6501 21.3383 17.8444 21.144 17.9662 20.9049ZM2.45818 18.8709C2.3822 18.7298 2.3822 18.5532 2.3822 18.2L2.3822 6.89251C2.3822 6.53673 2.3822 6.35885 2.45144 6.22296C2.51235 6.10343 2.60953 6.00625 2.72906 5.94534C2.86495 5.8761 3.04283 5.8761 3.39861 5.8761L14.7061 5.8761C15.0593 5.8761 15.236 5.8761 15.377 5.95208C15.4951 6.0157 15.6019 6.13093 15.6563 6.25354C15.7213 6.39996 15.7088 6.56382 15.6838 6.89152V6.89154C15.5766 8.29569 15.2476 9.67623 14.707 10.9812C14.0366 12.5997 13.054 14.0704 11.8152 15.3091C10.5765 16.5479 9.10584 17.5305 7.48732 18.2009C6.18233 18.7415 4.8018 19.0705 3.39764 19.1777C3.06992 19.2027 2.90606 19.2152 2.75964 19.1502C2.63703 19.0958 2.5218 18.989 2.45818 18.8709Z" fill="#0A0810"/>
                </svg>
            </div>
        </div>
        <div style="font-size:1.5em;margin:24px 0;text-align:center;"><strong>${title}</strong></div>
        <div style="display:flex;flex-wrap:wrap;">
            ${firstPartData
              .filter(item => item)
              .map(
                item => `
                <div style="width:50%; display:flex; margin:4px 0;">
                   <div style="width:50%;"><strong>${item?.inputTitle}</strong></div>
                   <div style="width:50%;">${item?.result}</div>
                </div>
            `,
              )
              .join('')}
        </div>
        <p>&nbsp;</p>
        ${tableHTML}
      </body>
      </html>
    `
}

const buildDataTableHTMLFor9734 = (jobData: JobData[]): string => {
  const INVOICE_NUMBER = 303586
  const SHIP_DATE = 303587
  const CUSTOMER = 303588
  const DESTINATION_AIRPORT = 303589
  const CUTOFF_DATE = 303590
  const DOMESTIC_INTERNATIONAL = 303591
  const PRODUCT = 303592
  const QUANTITY = 303593
  const DIPLOID_TRIPLOID = 303594
  const NUMBER_OF_BOXES = 303595
  const WEIGHT = 303596
  const COMMENTS = 303597

  const inputs = [
    INVOICE_NUMBER,
    SHIP_DATE,
    CUSTOMER,
    DESTINATION_AIRPORT,
    CUTOFF_DATE,
    DOMESTIC_INTERNATIONAL,
    PRODUCT,
    QUANTITY,
    DIPLOID_TRIPLOID,
    NUMBER_OF_BOXES,
    WEIGHT,
    COMMENTS,
  ]

  const groupsByEvtTask: Record<number, JobData[]> = {}
  jobData.forEach(item => {
    if (!inputs.includes(item.inputWorkflow)) {
      return
    }

    if (!groupsByEvtTask[item.evtTask]) groupsByEvtTask[item.evtTask] = []
    groupsByEvtTask[item.evtTask].push(item)
  })

  let tableHTML = `
  <figure class="table" style="width:100%;">
      <table style="font-size: 12px; border:1px solid black; border-collapse:collapse; width: 100%;">
          <thead>
              <tr>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Invoice Number</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Ship Date</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Customer</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Destination Airport</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Cutoff Date</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Domestic / International</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Product</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Quantity</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Diploid / Triploid</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Number of Boxes</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Weight</th>
                  <th style="border:1px solid black; padding:4px;text-align:left;">Comments</th>
              </tr>
          </thead>
          <tbody>
  `

  const getValue = (arr: JobData[], workflowId: number): string => {
    const found = arr.find(i => i.inputWorkflow === workflowId)

    return found &&
      found.result !== undefined &&
      found.result !== null &&
      found.result !== ''
      ? String(found.result)
      : '-'
  }

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const evtTaskId in groupsByEvtTask) {
    const group = groupsByEvtTask[evtTaskId]

    const invoiceNumberValue = getValue(group, INVOICE_NUMBER)
    const shipDateValue = getValue(group, SHIP_DATE)
    const customerValue = getValue(group, CUSTOMER)
    const destinationAirportValue = getValue(group, DESTINATION_AIRPORT)
    const cutoffDateValue = getValue(group, CUTOFF_DATE)
    const domesticInternationalValue = getValue(group, DOMESTIC_INTERNATIONAL)
    const productValue = getValue(group, PRODUCT)
    const quantityValue = getValue(group, QUANTITY)
    const diploidTriploidValue = getValue(group, DIPLOID_TRIPLOID)
    const numberOfBoxesValue = getValue(group, NUMBER_OF_BOXES)
    const weightValue = getValue(group, WEIGHT)
    const commentsValue = getValue(group, COMMENTS)

    tableHTML += `
      <tr>
        <td style="border:1px solid black; padding:4px;text-align:left;">${invoiceNumberValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${shipDateValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${customerValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${destinationAirportValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${cutoffDateValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${domesticInternationalValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${productValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${quantityValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${diploidTriploidValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${numberOfBoxesValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${weightValue}</td>
        <td style="border:1px solid black; padding:4px;text-align:left;">${commentsValue}</td>
      </tr>
    `
  }

  tableHTML += `
          </tbody>
      </table>
  </figure>
  `

  return tableHTML
}

export const generateHTMLTemplateForWorkflow9734 = async (job: Job) => {
  const { title, tasks } = job
  const dateInFormat = moment().format('YYYY-MM-DD')
  const jobData = getJobData(tasks)
  const tableHTML = buildDataTableHTMLFor9734(jobData)

  return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
      </head>
      <body style="font-size: 19px; font-family: Helvetica Neue; font-weight: 250; padding: 30px;">
        <div style="display: flex; justify-content: space-between; align-items: center; height: 100%;">
            <div class="doc-meta">${dateInFormat}</div>
            <div>
                <svg width="91" height="22" viewBox="0 0 91 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M35.5714 21.5986C33.7147 21.5986 32.1488 21.2179 30.8738 20.4566C29.5987 19.6841 28.6312 18.6204 27.9713 17.2657C27.3226 15.911 26.9983 14.3547 26.9983 12.5969C26.9983 10.7832 27.3338 9.19331 28.0049 7.82738C28.676 6.45026 29.6379 5.38663 30.8905 4.63649C32.1544 3.87515 33.67 3.49449 35.4371 3.49449C38.0991 3.49449 40.1627 4.3286 41.6279 5.99682C43.0931 7.66504 43.8537 9.95465 43.9096 12.8656C43.9096 13.112 43.7921 13.2351 43.5573 13.2351H31.0751C31.1534 15.1608 31.5728 16.5827 32.3334 17.5008C33.0939 18.4189 34.19 18.878 35.6217 18.878C36.6842 18.878 37.5511 18.6036 38.2221 18.055C38.8932 17.5064 39.3518 16.6835 39.5979 15.5863C39.6538 15.3736 39.7824 15.2672 39.9837 15.2672H43.1546C43.423 15.2672 43.5405 15.3624 43.5069 15.5527C43.3503 16.6723 42.9365 17.6912 42.2654 18.6092C41.6055 19.5273 40.7107 20.2551 39.5811 20.7925C38.4514 21.3299 37.1149 21.5986 35.5714 21.5986ZM31.1925 10.6152H39.5643C39.5084 9.83149 39.3238 9.10374 39.0107 8.43197C38.7087 7.76021 38.2613 7.22279 37.6685 6.81973C37.0757 6.40548 36.3431 6.19835 35.4707 6.19835C34.2292 6.19835 33.2561 6.57902 32.5515 7.34035C31.8468 8.10169 31.3939 9.19331 31.1925 10.6152Z" fill="#0A0810"/>
                    <path d="M54.0332 21.5986C52.2437 21.5986 50.717 21.2291 49.4531 20.4902C48.2004 19.7512 47.2385 18.71 46.5674 17.3665C45.9075 16.0117 45.5776 14.4219 45.5776 12.5969C45.5776 10.772 45.9075 9.17652 46.5674 7.81059C47.2385 6.44466 48.2116 5.38663 49.4866 4.63649C50.7617 3.87515 52.3052 3.49449 54.1171 3.49449C56.2758 3.49449 58.0094 4.05429 59.318 5.1739C60.6378 6.28232 61.3984 7.75461 61.5997 9.59077C61.6445 9.83709 61.5326 9.96024 61.2642 9.96024H58.2443C58.0206 9.96024 57.892 9.84828 57.8584 9.62436C57.6459 8.49355 57.1985 7.64825 56.5163 7.08844C55.834 6.52863 55.0343 6.24873 54.1171 6.24873C52.6296 6.24873 51.5223 6.79174 50.7953 7.87777C50.0794 8.96379 49.7215 10.5312 49.7215 12.5801C49.7215 14.6738 50.085 16.2413 50.812 17.2825C51.539 18.3237 52.6351 18.8556 54.1003 18.878C55.1741 18.8891 56.0633 18.5589 56.7679 17.8871C57.4725 17.2153 57.892 16.2469 58.0262 14.9817C58.0597 14.7466 58.1828 14.629 58.3953 14.629H61.4823C61.7507 14.629 61.8681 14.741 61.8346 14.9649C61.7116 16.2525 61.3145 17.4001 60.6434 18.4077C59.9723 19.4042 59.0776 20.1879 57.9591 20.7589C56.8406 21.3187 55.532 21.5986 54.0332 21.5986Z" fill="#0A0810"/>
                    <path d="M82.5108 21.5986C80.6877 21.5986 79.1386 21.2291 77.8635 20.4902C76.5997 19.7512 75.6322 18.7044 74.9611 17.3497C74.29 15.9949 73.9545 14.4051 73.9545 12.5801C73.9545 10.7664 74.29 9.17652 74.9611 7.81059C75.6322 6.44466 76.5997 5.38663 77.8635 4.63649C79.1386 3.87515 80.6877 3.49449 82.5108 3.49449C84.3227 3.49449 85.8606 3.86396 87.1245 4.6029C88.3884 5.34185 89.3503 6.38868 90.0102 7.74341C90.6701 9.09814 91 10.688 91 12.513C91 14.3379 90.6701 15.9334 90.0102 17.2993C89.3503 18.6652 88.3884 19.7233 87.1245 20.4734C85.8606 21.2235 84.3227 21.5986 82.5108 21.5986ZM82.5108 18.878C83.976 18.878 85.0665 18.3517 85.7823 17.2993C86.4981 16.2469 86.8561 14.6738 86.8561 12.5801C86.8561 10.52 86.4926 8.94699 85.7655 7.86097C85.0385 6.76375 83.9424 6.21514 82.4772 6.21514C80.9897 6.22634 79.8824 6.78055 79.1554 7.87777C78.4395 8.96379 78.0816 10.5424 78.0816 12.6137C78.0816 14.685 78.4507 16.2469 79.1889 17.2993C79.9271 18.3517 81.0344 18.878 82.5108 18.878Z" fill="#0A0810"/>
                    <path d="M70.1067 21.4978C69.2008 21.4978 68.3563 21.3355 67.5734 21.0108C66.7905 20.6749 66.1529 20.1263 65.6608 19.365C65.1799 18.5925 64.9394 17.568 64.9394 16.2916L64.9562 6.80294H63.0771C62.8423 6.80294 62.7248 6.67978 62.7248 6.43347V5.57421V4.76353C62.7248 4.55123 62.8997 4.37516 63.1101 4.3486C64.506 4.17246 66.4798 3.33689 66.6537 0.391959C66.6665 0.176393 66.84 0 67.0557 0H68.5499C68.7656 0 68.9405 0.175056 68.9405 0.390999V3.89755H72.7911C73.0372 3.89755 73.1602 4.0151 73.1602 4.25022V6.43347C73.1602 6.67978 73.0372 6.80294 72.7911 6.80294H68.9324L68.9659 16.1909C68.9659 17.0978 69.1393 17.7248 69.486 18.0718C69.8327 18.4077 70.3249 18.5757 70.9624 18.5757C71.3091 18.5757 71.6279 18.5589 71.9187 18.5253C72.2207 18.4805 72.4891 18.4245 72.724 18.3573C72.9924 18.279 73.1266 18.3797 73.1266 18.6596V20.6245C73.1266 20.8373 73.0372 20.9716 72.8582 21.0276C72.4444 21.162 71.997 21.2739 71.516 21.3635C71.0463 21.4531 70.5765 21.4978 70.1067 21.4978Z" fill="#0A0810"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.9662 20.9049C18.1047 20.6331 18.1047 20.2773 18.1047 19.5658L18.1047 5.52671C18.1047 4.81516 18.1047 4.45938 17.9662 4.18761C17.8444 3.94855 17.6501 3.75418 17.411 3.63237C17.1392 3.4939 16.7835 3.4939 16.0719 3.4939L2.03281 3.4939C1.32126 3.4939 0.965486 3.4939 0.69371 3.63237C0.454649 3.75418 0.260286 3.94854 0.138478 4.1876C1.32073e-06 4.45938 1.28963e-06 4.81516 1.22742e-06 5.5267L0 19.5658C-6.21225e-08 20.2773 -9.32254e-08 20.6331 0.138477 20.9049C0.260284 21.144 0.454647 21.3383 0.693708 21.4601C0.965484 21.5986 1.32126 21.5986 2.03281 21.5986L16.0719 21.5986C16.7834 21.5986 17.1392 21.5986 17.411 21.4601C17.6501 21.3383 17.8444 21.144 17.9662 20.9049ZM2.45818 18.8709C2.3822 18.7298 2.3822 18.5532 2.3822 18.2L2.3822 6.89251C2.3822 6.53673 2.3822 6.35885 2.45144 6.22296C2.51235 6.10343 2.60953 6.00625 2.72906 5.94534C2.86495 5.8761 3.04283 5.8761 3.39861 5.8761L14.7061 5.8761C15.0593 5.8761 15.236 5.8761 15.377 5.95208C15.4951 6.0157 15.6019 6.13093 15.6563 6.25354C15.7213 6.39996 15.7088 6.56382 15.6838 6.89152V6.89154C15.5766 8.29569 15.2476 9.67623 14.707 10.9812C14.0366 12.5997 13.054 14.0704 11.8152 15.3091C10.5765 16.5479 9.10584 17.5305 7.48732 18.2009C6.18233 18.7415 4.8018 19.0705 3.39764 19.1777C3.06992 19.2027 2.90606 19.2152 2.75964 19.1502C2.63703 19.0958 2.5218 18.989 2.45818 18.8709Z" fill="#0A0810"/>
                </svg>
            </div>
        </div>
        <div style="font-size:1.5em;margin:24px 0;text-align:center;"><strong>${title}</strong></div>
        ${tableHTML}
      </body>
      </html>
    `
}

export default generateHTMLTemplate
